import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { EllipsisIcon } from 'lucide-react';
import { DateTime } from 'luxon';
import { AhsRenewalDialog, type AhsPackage } from '@/components/AhsRenewalDialog';
import { statusBadgeClass } from '@/components/statusBadges';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button, buttonVariants } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useApi, useNavigate } from '@/lib/legacy';
import { notifyError } from '@/lib/notify';
import {
  formatPolicyDate,
  isAhsProduct,
  policyStatus,
  renewalAvailability,
  showsTermState,
  termRange,
  termLabel,
  termState,
  termZoneNote,
} from '@/lib/policy';
import { cn } from '@/lib/utils';
import LoaderModal from '../../../organisms/MCEModals/LoaderModal';
import { usePolicyDetails, type PolicyRecord } from '../context/PolicyDetailsContext';
import { sectionUrl } from '../sections';

/**
 * The header's sticky band: the strip that clears the app's header, then the breadcrumb ("Policies",
 * and the policy number once there is one), then whatever the page puts under it. The whole band,
 * breadcrumb to section nav, sticks.
 */
export function PolicyHeaderBand({ policyNumber, children }: { policyNumber?: string | undefined; children?: ReactNode }) {
  const navigate = useNavigate();
  return (
    <div className="sticky top-0 z-[1100] w-full bg-background">
      <div className="h-14 w-full" />
      <div className="flex flex-col gap-3 border-b px-7 pt-3.5">
        <Breadcrumb>
          <BreadcrumbList className="text-[13px] text-muted-foreground">
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/policies"
                onClick={(event) => {
                  event.preventDefault();
                  navigate.back('/policies');
                }}
              >
                Policies
              </BreadcrumbLink>
            </BreadcrumbItem>
            {policyNumber ? (
              <>
                <BreadcrumbSeparator>/</BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage>{policyNumber}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            ) : null}
          </BreadcrumbList>
        </Breadcrumb>
        {children}
      </div>
    </div>
  );
}

/** The loaded policy's title row (insured, status chip, actions) and term line, and the renewal flows. */
export function PolicyHeader({ policy }: { policy: PolicyRecord }) {
  const navigate = useNavigate();
  const { createRenewalQuote, getPackages, getInferredPackage } = useApi();
  const [creatingRenewal, setCreatingRenewal] = useState(false);
  const [ahsRenewalOpen, setAhsRenewalOpen] = useState(false);
  const [packages, setPackages] = useState<AhsPackage[]>([]);
  const [inferredPackageId, setInferredPackageId] = useState<string | null>(null);

  /* "now" is the page's, shared with the Policy Details cards and taken when the policy loaded, so
     the chip, the term line and the renewal item read the same until a reload. The renewal handlers
     still check renewal against the time they run. */
  const { now } = usePolicyDetails();
  const { status, term, renewal, isAhs, range, zoneNote } = useMemo(
    () => ({
      status: policyStatus(policy, now),
      // An Inactive policy shows no term state.
      term: showsTermState(policy, now) ? termState(policy, now) : null,
      renewal: renewalAvailability(policy, now),
      isAhs: isAhsProduct(policy),
      range: termRange(policy),
      zoneNote: termZoneNote(policy),
    }),
    [policy, now]
  );
  const renewalOpenNow = useCallback(() => renewalAvailability(policy, DateTime.now()).available, [policy]);

  const createRenewal = useCallback(async () => {
    if (!renewalOpenNow()) return;
    setCreatingRenewal(true);
    try {
      const response = await createRenewalQuote(policy.id);
      if (response) {
        navigate.to(`/quotes/${response.id}`);
      }
    } catch {
      notifyError('Renewal Quote failed');
    } finally {
      setCreatingRenewal(false);
    }
  }, [renewalOpenNow, createRenewalQuote, policy.id, navigate]);

  const openAhsRenewal = useCallback(async () => {
    if (!renewalOpenNow()) return;
    try {
      const [found, inferred] = await Promise.all([getPackages(), getInferredPackage(policy.id)]);
      setPackages(found || []);
      setInferredPackageId(inferred?.id || null);
    } catch {
      setPackages([]);
      setInferredPackageId(null);
    }
    setAhsRenewalOpen(true);
  }, [renewalOpenNow, getPackages, getInferredPackage, policy.id]);

  const productName = policy.product?.name;
  const renewalLabel = isAhs ? 'Renew AHS policy' : 'Create renewal quote';

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">{policy.insured}</h1>
        <Badge className={cn(statusBadgeClass(status), 'h-auto rounded-md px-2 py-1 text-[11px] font-semibold uppercase')}>{status}</Badge>
        <div className="ml-auto flex flex-wrap gap-2">
          <Link to={sectionUrl(policy.id, '/documents')} className={buttonVariants({ variant: 'outline' })}>
            Preview document
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger render={<Button variant="outline" size="icon" aria-label="More actions" />}>
              <EllipsisIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-auto min-w-[200px]">
              <DropdownMenuItem
                disabled={!renewal.available}
                onClick={() => (isAhs ? openAhsRenewal() : createRenewal())}
                className="flex-col items-start gap-0.5"
              >
                <span>{renewalLabel}</span>
                {renewal.available ? null : (
                  <span className="text-xs text-muted-foreground" title={zoneNote}>{`Available from ${formatPolicyDate(renewal.opensOn)}`}</span>
                )}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[13px] text-muted-foreground">
        {productName ? <span>{productName}</span> : null}
        <span title={zoneNote}>{range}</span>
        {term ? (
          <span className="font-semibold text-foreground" title={term.kind === 'running' ? undefined : zoneNote}>
            {termLabel(term)}
          </span>
        ) : null}
      </div>
      {creatingRenewal ? <LoaderModal loading={creatingRenewal} setLoading={setCreatingRenewal} /> : null}
      {isAhs ? (
        <AhsRenewalDialog
          policy={policy}
          open={ahsRenewalOpen}
          packages={packages}
          inferredPackageId={inferredPackageId}
          onClose={() => setAhsRenewalOpen(false)}
          onSuccess={(renewed) => {
            setAhsRenewalOpen(false);
            navigate.to(`/policies/${renewed.id}`);
          }}
        />
      ) : null}
    </>
  );
}
